import type { ProcessGraph, RaciEntry, Step, Transition } from '@process-hub/shared';

/**
 * Pure edit operations on a ProcessGraph for the step editor. Every function returns a new
 * graph and never mutates its input, so the editor can keep an undo-friendly "original vs
 * working copy" and validate the working copy with the shared validateGraph on every change.
 */

export function newStepKey(graph: ProcessGraph, name: string): string {
  const base =
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/^[^a-z]+/, '')
      .slice(0, 60) || 'step';
  const taken = new Set(graph.steps.map((s) => s.key));
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) if (!taken.has(`${base}_${n}`)) return `${base}_${n}`;
}

export function blankStep(graph: ProcessGraph, name: string, role: string): Step {
  return {
    key: newStepKey(graph, name),
    name,
    description: null,
    role,
    performedBy: 'human',
    stepType: 'task',
    systems: [],
    touchMinutes: null,
    waitMinutes: null,
    notes: null,
    raci: [{ role, letter: 'R' }],
    requiresJudgment: false,
    involvesNegotiation: false,
    affectsClientTrust: false,
    automationSignals: null,
    whyNotAutomate: null,
    overrideReason: null,
  };
}

/** Inserts after `afterKey` (or at the end) and links it into a plain sequence when possible. */
export function addStep(graph: ProcessGraph, step: Step, afterKey: string | null): ProcessGraph {
  const idx = afterKey ? graph.steps.findIndex((s) => s.key === afterKey) : -1;
  const steps = [...graph.steps];
  steps.splice(idx === -1 ? steps.length : idx + 1, 0, step);
  let transitions = graph.transitions;
  if (afterKey) {
    // Re-route the single unlabeled outgoing edge of the previous step through the new one.
    const outs = transitions.filter((t) => t.from === afterKey);
    if (outs.length === 1 && outs[0].condition === null) {
      const [only] = outs;
      transitions = [...transitions.filter((t) => t !== only), { from: afterKey, to: step.key, condition: null, isDefault: false }, { ...only, from: step.key }];
    } else {
      transitions = [...transitions, { from: afterKey, to: step.key, condition: null, isDefault: false }];
    }
  }
  return { ...graph, steps, transitions };
}

/** Removes the step and every transition touching it; bridges a simple in→out sequence. */
export function removeStep(graph: ProcessGraph, key: string): ProcessGraph {
  const ins = graph.transitions.filter((t) => t.to === key);
  const outs = graph.transitions.filter((t) => t.from === key);
  let transitions = graph.transitions.filter((t) => t.from !== key && t.to !== key);
  if (ins.length === 1 && outs.length === 1 && outs[0].condition === null) {
    transitions = [...transitions, { ...ins[0], to: outs[0].to }];
  }
  return { ...graph, steps: graph.steps.filter((s) => s.key !== key), transitions };
}

export function moveStep(graph: ProcessGraph, key: string, direction: -1 | 1): ProcessGraph {
  const i = graph.steps.findIndex((s) => s.key === key);
  const j = i + direction;
  if (i === -1 || j < 0 || j >= graph.steps.length) return graph;
  const steps = [...graph.steps];
  [steps[i], steps[j]] = [steps[j], steps[i]];
  return { ...graph, steps };
}

export function updateStep(graph: ProcessGraph, key: string, patch: Partial<Omit<Step, 'key'>>): ProcessGraph {
  return { ...graph, steps: graph.steps.map((s) => (s.key === key ? { ...s, ...patch } : s)) };
}

export function upsertTransition(graph: ProcessGraph, index: number | null, t: Transition): ProcessGraph {
  const transitions = [...graph.transitions];
  if (index === null) transitions.push(t);
  else transitions[index] = t;
  return { ...graph, transitions };
}

export function removeTransition(graph: ProcessGraph, index: number): ProcessGraph {
  return { ...graph, transitions: graph.transitions.filter((_, i) => i !== index) };
}

/** Marks one outgoing edge of `fromKey` as the default and clears the others. */
export function setDefaultTransition(graph: ProcessGraph, fromKey: string, index: number): ProcessGraph {
  return { ...graph, transitions: graph.transitions.map((t, i) => (t.from === fromKey ? { ...t, isDefault: i === index } : t)) };
}

export function setRaci(graph: ProcessGraph, key: string, raci: RaciEntry[]): ProcessGraph {
  return updateStep(graph, key, { raci });
}

/** Text ↔ list helpers for the form. */
export const systemsToText = (s: Step['systems']): string => s.map((x) => (x.usage === 'both' ? x.name : `${x.name} (${x.usage})`)).join(', ');
export function textToSystems(text: string): Step['systems'] {
  return text
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      const m = /^(.*?)\s*\((reads|writes|both)\)$/i.exec(p);
      return m ? { name: m[1].trim(), usage: m[2].toLowerCase() as 'reads' | 'writes' | 'both' } : { name: p, usage: 'both' as const };
    });
}

export const minutesFromInput = (v: string): number | null => (v.trim() === '' ? null : Math.max(0, Math.round(Number(v)) || 0));

export function isDirty(a: ProcessGraph, b: ProcessGraph): boolean {
  return JSON.stringify(a) !== JSON.stringify(b);
}
